import { useEffect, useMemo } from "react";
import { Table, Popconfirm, Space, Button, Tag, Badge } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { IBannerObject } from "../../../../interfaces/banner.interface";
import { formatLocalDate } from "../../../../utils/formatDate";
import { bannerSeverityMeta } from "../../bannerDisplay";
import { resolveLocalizedText } from "../../bannerLocales";
import {
  deleteBannerThunk,
  listBannersThunk,
  selectBanners,
  selectBannersLastUpdatedKey,
  selectBannersLoading,
} from "../../bannersSlice";

interface BannersTableProps {
  onEdit: (banner: IBannerObject) => void;
}

export default function BannersTable({ onEdit }: BannersTableProps) {
  const dispatch = useAppDispatch();
  const banners = useAppSelector(selectBanners);
  const loading = useAppSelector(selectBannersLoading);
  const lastUpdatedKey = useAppSelector(selectBannersLastUpdatedKey);

  useEffect(() => {
    dispatch(listBannersThunk({}));
  }, [dispatch, lastUpdatedKey]);

  const columns: ColumnsType<IBannerObject> = useMemo(
    () => [
      {
        title: "Title",
        dataIndex: "title",
        key: "title",
        render: (v, row) => resolveLocalizedText(v, row.sourceLocale) || "-",
      },
      {
        title: "Message",
        dataIndex: "message",
        key: "message",
        ellipsis: true,
        render: (v, row) => resolveLocalizedText(v, row.sourceLocale),
      },
      {
        title: "Severity",
        dataIndex: "severity",
        key: "severity",
        render: (v) => {
          const m = bannerSeverityMeta(v);
          return <Tag color={m.color}>{m.label}</Tag>;
        },
      },
      {
        title: "Active",
        dataIndex: "isActive",
        key: "isActive",
        render: (v) => (
          <Badge
            status={v ? "success" : "default"}
            text={v ? "Active" : "Inactive"}
          />
        ),
      },
      {
        title: "Start",
        dataIndex: "startAt",
        key: "startAt",
        render: (v) => (v ? formatLocalDate(new Date(v), true) : "-"),
      },
      {
        title: "End",
        dataIndex: "endAt",
        key: "endAt",
        render: (v) => (v ? formatLocalDate(new Date(v), true) : "-"),
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, row) => (
          <Space>
            <Button size="small" onClick={() => onEdit(row)}>
              Edit
            </Button>
            <Popconfirm
              title="Delete banner?"
              description="This action cannot be undone."
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              icon={<ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />}
              onConfirm={() =>
                dispatch(deleteBannerThunk({ bannerId: row._id }))
              }
            >
              <Button size="small" danger>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [dispatch, onEdit]
  );

  return (
    <Table<IBannerObject>
      rowKey={(r) => r._id}
      dataSource={banners}
      loading={loading}
      columns={columns}
      size="middle"
      scroll={{ x: "max-content" }}
    />
  );
}
